import React, {useState, useEffect} from "react";
import {Box, Typography, Paper, Grid, Card, CardContent, CircularProgress, Alert, Tabs, Tab, Chip} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {getFeedRecords, getSamplingRecords, getMortalityRecords, getWaterQualityRecords, getHarvestRecords} from "../../api/pond";

interface PondPerformanceAnalyticsProps {
  pondId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`analytics-tabpanel-${index}`} aria-labelledby={`analytics-tab-${index}`} {...other}>
      {value === index && <Box sx={{p: 2}}>{children}</Box>}
    </div>
  );
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82CA9D"];

const PondPerformanceAnalytics: React.FC<PondPerformanceAnalyticsProps> = ({pondId}) => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState({
    feedRecords: [] as any[],
    samplingRecords: [] as any[],
    mortalityRecords: [] as any[],
    waterQualityRecords: [] as any[],
    harvestRecords: [] as any[],
  });

  useEffect(() => {
    fetchAllData();
  }, [pondId]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [feedData, samplingData, mortalityData, waterQualityData, harvestData] = await Promise.all([
        getFeedRecords(pondId).catch(() => ({data: []})),
        getSamplingRecords(pondId).catch(() => ({data: []})),
        getMortalityRecords(pondId).catch(() => ({data: []})),
        getWaterQualityRecords(pondId).catch(() => ({data: []})),
        getHarvestRecords(pondId).catch(() => ({data: []})),
      ]);

      setData({
        feedRecords: feedData.data || [],
        samplingRecords: samplingData.data || [],
        mortalityRecords: mortalityData.data || [],
        waterQualityRecords: waterQualityData.data || [],
        harvestRecords: harvestData.data || [],
      });
    } catch (err) {
      setError("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  // Process data for charts
  const processedFeedData = data.feedRecords
    .sort((a, b) => new Date(a.feedDate).getTime() - new Date(b.feedDate).getTime())
    .map((record) => ({
      date: new Date(record.feedDate).toLocaleDateString(),
      totalFeed: parseFloat(record.totalQuantity || 0),
      feed1: parseFloat(record.feed1 || 0),
      feed2: parseFloat(record.feed2 || 0),
      feed3: parseFloat(record.feed3 || 0),
      feed4: parseFloat(record.feed4 || 0),
    }));

  const processedSamplingData = data.samplingRecords
    .sort((a, b) => new Date(a.sampledAt).getTime() - new Date(b.sampledAt).getTime())
    .map((record) => ({
      date: new Date(record.sampledAt).toLocaleDateString(),
      averageWeight: parseFloat(record.averageWeight || 0),
    }));

  const processedMortalityData = data.mortalityRecords
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((record) => ({
      date: new Date(record.date).toLocaleDateString(),
      quantity: record.quantity || 0,
      age: record.age || 0,
    }));

  const processedWaterQualityData = data.waterQualityRecords
    .sort((a, b) => new Date(a.recordDate).getTime() - new Date(b.recordDate).getTime())
    .map((record) => ({
      date: new Date(record.recordDate).toLocaleDateString(),
      temperature: parseFloat(record.temperature || 0),
      ph: parseFloat(record.ph || 0),
      dissolvedOxygen: parseFloat(record.dissolvedOxygen || 0),
      ammonia: parseFloat(record.ammonia || 0),
      nitrite: parseFloat(record.nitrite || 0),
      nitrate: parseFloat(record.nitrate || 0),
    }));

  const processedHarvestData = data.harvestRecords
    .sort((a, b) => new Date(a.harvestDate).getTime() - new Date(b.harvestDate).getTime())
    .map((record) => ({
      date: new Date(record.harvestDate).toLocaleDateString(),
      count: record.count || 0,
      totalWeight: parseFloat(record.totalWeight || 0),
      survivalNumber: parseFloat(record.survivalNumber || 0),
    }));

  // Calculate performance metrics
  const totalFeedUsed = processedFeedData.reduce((sum, record) => sum + record.totalFeed, 0);
  const totalMortality = processedMortalityData.reduce((sum, record) => sum + record.quantity, 0);
  const totalHarvested = processedHarvestData.reduce((sum, record) => sum + record.count, 0);
  const totalHarvestedWeight = processedHarvestData.reduce((sum, record) => sum + record.totalWeight, 0);

  const latestSampling = processedSamplingData[processedSamplingData.length - 1];
  const currentABW = latestSampling?.averageWeight || 0;

  // Calculate FCR if we have data
  let fcr = 0;
  if (totalFeedUsed > 0 && (currentABW > 0 || totalHarvestedWeight > 0)) {
    const currentBiomass = (currentABW * (100000 - totalMortality - totalHarvested)) / 1000; // Assuming 100k initial stock
    const totalBiomass = currentBiomass + totalHarvestedWeight;
    fcr = totalBiomass > 0 ? totalFeedUsed / totalBiomass : 0;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600} color="primary">
        Pond Performance Analytics
      </Typography>

      {/* Performance Summary Cards */}
      <Grid container spacing={2} sx={{mb: 3}}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Feed Used
              </Typography>
              <Typography variant="h6">{totalFeedUsed.toFixed(2)} kg</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Current ABW
              </Typography>
              <Typography variant="h6">{currentABW.toFixed(2)} g</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                FCR
              </Typography>
              <Typography variant="h6">{fcr > 0 ? fcr.toFixed(3) : "-"}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Harvested
              </Typography>
              <Typography variant="h6">{totalHarvestedWeight.toFixed(2)} kg</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Analytics Tabs */}
      <Paper sx={{width: "100%"}}>
        <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
          <Tab label="Feed Analysis" />
          <Tab label="Growth Trends" />
          <Tab label="Mortality Analysis" />
          <Tab label="Water Quality" />
          <Tab label="Harvest Data" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Daily Feed Consumption
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={processedFeedData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="totalFeed" stroke="#8884d8" name="Total Feed (kg)" />
                </LineChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Feed Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={processedFeedData.slice(-10)}>
                  {" "}
                  {/* Last 10 records */}
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="feed1" fill="#8884d8" name="Feed 1" />
                  <Bar dataKey="feed2" fill="#82ca9d" name="Feed 2" />
                  <Bar dataKey="feed3" fill="#ffc658" name="Feed 3" />
                  <Bar dataKey="feed4" fill="#ff7300" name="Feed 4" />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Growth Curve (Average Body Weight)
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={processedSamplingData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="averageWeight" stroke="#8884d8" fill="#8884d8" name="ABW (g)" />
                </AreaChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Daily Mortality
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={processedMortalityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="quantity" fill="#ff6b6b" name="Mortality Count" />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Mortality by Age
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={processedMortalityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="age" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="quantity" stroke="#ff6b6b" name="Mortality Count" />
                </LineChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Water Quality Parameters
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={processedWaterQualityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="temperature" stroke="#ff7300" name="Temperature (°C)" />
                  <Line type="monotone" dataKey="ph" stroke="#8884d8" name="pH" />
                  <Line type="monotone" dataKey="dissolvedOxygen" stroke="#00C49F" name="DO (mg/L)" />
                </LineChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Nitrogen Compounds
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={processedWaterQualityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="ammonia" stroke="#ff6b6b" name="Ammonia (mg/L)" />
                  <Line type="monotone" dataKey="nitrite" stroke="#ffc658" name="Nitrite (mg/L)" />
                  <Line type="monotone" dataKey="nitrate" stroke="#82ca9d" name="Nitrate (mg/L)" />
                </LineChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Harvest Quantities
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={processedHarvestData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="totalWeight" fill="#00C49F" name="Total Weight (kg)" />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Harvest Average Weight
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={processedHarvestData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="averageWeight" stroke="#8884d8" name="Avg Weight (g)" />
                </LineChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Data Summary */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          Data Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Chip label={`${processedFeedData.length} Feed Records`} color="primary" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Chip label={`${processedSamplingData.length} Sampling Records`} color="secondary" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Chip label={`${processedMortalityData.length} Mortality Records`} color="error" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Chip label={`${processedWaterQualityData.length} Water Quality Records`} color="info" />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PondPerformanceAnalytics;
